import { isSimpleMode } from '@/modules/simpleMode'
import type {ListFilter} from '@/modules/listSchemas'
import type {PendingCommand} from '@/shared/api/pendingCommand'
export type ReportKind='period-inventory'|'profitability'|'stock-analysis'|'scrap-loss'
export const reportBase='/api/v1/reports/'
export const reportingBase='/api/v1/reporting'

/** Kinds shown on the shell bell. */
export const inAppReminderKinds = ['low_stock', 'expiry', 'stagnant', 'collection'] as const

/** Unread total is `count` on this filtered list; page size is not that total. */
export function inAppUnreadAlertsPath() {
  const query = new URLSearchParams({
    active: 'true', unread: 'true', in_app: 'true', page: '1', page_size: '1',
  })
  return `${reportingBase}/alerts?${query}`
}
export type ReportRow=Record<string,string|number|boolean|null|undefined>
export type ReportResult={rows:ReportRow[];count:number;page:number;page_size:number;calculated_at:string;
  totals:ReportRow;basis:string}
export function internalSource(value:unknown):string|undefined{
  return typeof value==='string'&&/^\/(?:records|reports|finance|warehouse|settings|approvals)(?:\/|\?|$)/.test(value)
    &&!/[\\\r\n]/.test(value)?value:undefined
}
export function reportFilters(kind:ReportKind):ListFilter[]{
  const fields:ListFilter[]=[{key:'date_from',label:'开始日期',labelEn:'Start date',kind:'date'},
    {key:'date_to',label:'结束日期',labelEn:'End date',kind:'date'},
    {key:'warehouse_id',label:'仓库',labelEn:'Warehouse',kind:'select',source:'warehouses'},
    {key:'product_id',label:'商品',labelEn:'Product',kind:'select',source:'products'}]
  if(kind==='profitability')fields.push({key:'customer_id',label:'客户',labelEn:'Customer',kind:'select',source:'customers'},
    {key:'origin_outbound_id',label:'原销售出库',labelEn:'Original sales outbound',kind:'select',source:'revenue_sales_outbounds'},
    ...(!isSimpleMode() ? [{key:'origin_order_id',label:'原收入订单',labelEn:'Original revenue order',kind:'select' as const,source:'revenue_sales_orders'},
      {key:'order_id',label:'销售订单',labelEn:'Sales order',kind:'select' as const,source:'sales_orders'}] : []),
    {key:'group_by',label:'汇总方式',labelEn:'Grouping',kind:'select',choices:[{id:'order',label:isSimpleMode()?'单据':'订单',labelEn:isSimpleMode()?'Document':'Order'},
      {id:'product',label:'商品',labelEn:'Product'},{id:'customer',label:'客户',labelEn:'Customer'},{id:'month',label:'月份',labelEn:'Month'}]})
  if(kind==='stock-analysis')fields.push({key:'rank_by',label:'热销排名依据',labelEn:'Ranking basis',kind:'select',choices:[
    {id:'quantity_kg',label:'实际销售KG',labelEn:'Actual sales KG'},{id:'revenue',label:'销售额',labelEn:'Revenue'},
    {id:'gross_profit',label:'毛利额',labelEn:'Gross profit'}]},
    {key:'classification',label:'动销状态',labelEn:'Movement state',kind:'select',choices:[
      {id:'all',label:'全部',labelEn:'All'},{id:'moving',label:'动销',labelEn:'Moving'},{id:'slow',label:'呆滞',labelEn:'Slow'},
      {id:'new',label:'新品观察',labelEn:'New product observation'},{id:'normal',label:'正常观察',labelEn:'Normal observation'},
      {id:'unknown',label:'待核实',labelEn:'Unknown'}]})
  return fields
}
export type Rule={id:number;version:number;domain:'inventory'|'collection';scope:'company'|'product'|'warehouse_product'|'customer_level'|'customer';
  product_id:number|null;warehouse_id:number|null;customer_level_id:number|null;customer_id:number|null;
  product_name?:string;warehouse_name?:string;customer_level_name?:string;customer_name?:string;
  stagnant_days:number|null;new_product_days:number|null;safety_stock_kg:string|null;followup_days:number|null;enabled:boolean|null}
export type Alert={id:number;version:number;kind:'low_stock'|'expiry'|'stagnant'|'collection';
  source_type:string;source_id:number;source_no:string;warehouse_id:number|null;product_id:number|null;customer_id:number|null;customer_name?:string|null;
  active:boolean;level:string;audience:string;can_follow:boolean;read:boolean;first_seen_at:string;last_seen_at:string;
  basis:Record<string,string|number|string[]|null|{source:string;rule_id:number|null;version:number;field_sources:Record<string,{source:string;version:number}>}>;
  snoozed_until:string|null;resolved_at:string|null;events:{id:number;action:string;reason:string;created_at:string;actor_name:string;until:string|null}[]}
export type ReportingIntent={action:string;id?:number;body:Record<string,unknown>}
export function reportingIntent(value:PendingCommand):ReportingIntent|null{
  const match=value.path.match(/^\/api\/v1\/reporting\/(rules(?:\/([1-9]\d*)\/save)?|alerts\/evaluate|alerts\/([1-9]\d*)\/(read|follow-up|snooze))$/)
  if(!match)return null
  try{
    const body=JSON.parse(value.body) as Record<string,unknown>
    if(!body||typeof body!=='object'||Array.isArray(body)||typeof body.reason!=='string'||!body.reason.trim())return null
    const id=match[2]?Number(match[2]):match[3]?Number(match[3]):undefined
    if(id!==undefined&&!Number.isSafeInteger(id))return null
    const action=match[1]==='rules'?'rule.create':match[2]?'rule.save':match[1]==='alerts/evaluate'?'alert.evaluate':`alert.${match[4]}`
    if(action!=='alert.evaluate'&&(!Number.isSafeInteger(body.expected_version)||Number(body.expected_version)<(id?1:0)))return null
    return {action,id,body}
  }catch{return null}
}

/** Use the server's explicit origin scope, never infer identity from signed group keys. */
export function profitOrigin(row:ReportRow):{key:'origin_outbound_id'|'origin_order_id';id:string}|null{
  if(typeof row.source_path!=='string'||!row.source_path.startsWith('/api/v1/reports/profitability/sources?'))return null
  const query=new URL(row.source_path,'http://local.invalid').searchParams
  for(const key of ['origin_outbound_id','origin_order_id'] as const){
    const id=query.get(key)
    if(id&&/^[1-9]\d*$/.test(id))return {key,id}
  }
  return null
}
